// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss, Roboto } from "@mcmc/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    links = "links",
}

interface TitleProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.links]: LinkModel[];
    };
    extendedStyles?: SectionTitleStyles;
}

export interface SectionTitleStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linksWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const sectionTitleStyles: SectionTitleStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            margin: 0 -5px 25px -5px;
            @media print {
                margin: 5px -5px;
                break-after: avoid;
            }
        `,
    },
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 0;
            padding: 0 5px;

            .title-slash {
                color: ${getColor("primary")};
                margin-right: 5px;
            }
        `,
    },
    linksWrapper: {
        css: css`
            flex: 1 1 auto;
            display: inline-flex;
            justify-content: flex-end;
            flex-wrap: wrap;
            margin-left: -10px;
            padding: 0 5px;
        `,
    },
    linkWrapper: {
        css: css`
            position: relative;
            padding: 0 10px;
            &::after {
                display: block;
                content: "|";
                font-weight: bold;
                position: absolute;
                right: -1px;
                top: 50%;
                transform: translateY(-50%);
            }

            &:last-child {
                padding-right: 0;
                &::after {
                    display: none;
                }
            }
        `,
    },
    link: {
        typographyProps: {
            ...Roboto,
            size: 16,
            lineHeight: "21px",
            weight: 500,
            css: css`
                letter-spacing: 0;
            `,
        },
    },
};

const SectionTitleWidget: React.FunctionComponent<TitleProps> = ({ fields, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(sectionTitleStyles, extendedStyles));
    const links = useGetLinks(fields.links || [], o => o.fields.destination);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title} aria-label={fields.title}>
                <span className="title-slash" aria-hidden="true">
                    |
                </span>
                {fields.title}
            </Typography>
            {links.length > 0 && (
                <StyledWrapper {...styles.linksWrapper}>
                    {links.map(
                        (link, index) =>
                            link?.url && (
                                // eslint-disable-next-line react/no-array-index-key
                                <StyledWrapper key={`${link.url}${index}`} {...styles.linkWrapper}>
                                    <Link
                                        {...styles.link}
                                        href={link.url}
                                        target={fields.links[index].fields.openInNewWindow ? "_blank" : undefined}
                                    >
                                        {fields.links[index].fields.overriddenTitle || link.title}
                                    </Link>
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SectionTitleWidget,
    definition: {
        group: "Basic",
        icon: "PageTitle",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export function createSectionTitleWidget(
    title: string,
    titleLink?: LinkFieldValue,
    titleLinkLabel?: string,
    extendedStyles?: SectionTitleStyles,
    openInNewWindow?: boolean,
) {
    return createWidgetElement("SectionTitleWidget/SectionTitleWidget", {
        fields: {
            title,
            links: titleLink
                ? [{ fields: { destination: titleLink, overriddenTitle: titleLinkLabel, openInNewWindow } }]
                : [],
        },
        extendedStyles,
    } as any);
}

export default widgetModule;
